import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  BookingState,
  ICalendarEventType,
  NotificationIdMap,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  PositiveIcons,
  RecurrenceActions,
  TypeOfUser,
  getClickedNotification,
  getNotification,
  getNotificationLoad,
  getNotificationState,
  getTypeOfUser,
  notificationActions,
  resetStatus,
  setClickedNotification,
  successOrSelector,
} from '@pharmaplan/common';
import { INotificationsData } from '@pharmaplan/common/models/NotificationsModel';

import { getTodayWithoutOffset } from '../components/Reports/helpers';
import { Constants } from '../helpers/Constants';

import useDrawerNavigation from './useDrawerNavigation';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import useSelfService from './useSelfService';

const { series } = RecurrenceActions;
const { pharmacist, pharmacy } = TypeOfUser;

const { requested, posted, booked: pharmacyBooked } = PharmacyLegendVariant;
const { booked } = PharmacistLegendVariant;

const statusMap = [posted, requested, booked];
const { calendarDashboard } = Constants.paths;

const initValue = {
  isRequested: false,
  item: null,
};

interface ICommonDrawerOpen {
  id: string;
  professionalId?: string;
  inType: ICalendarEventType;
  isAutoSyncRecord?: boolean;
  inBookingState: BookingState;
  longTermShiftContractId?: string;
}
interface IUseNotifications {
  closeNotification: () => void;
  handleMarkAsRead?: (id: string) => void;
}

const useNotification = ({
  closeNotification,
  handleMarkAsRead,
}: IUseNotifications) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { openDrawer } = useDrawerNavigation();
  const { isSelfService } = useSelfService();
  const visitSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        notificationActions.getNotification,
        notificationActions.getNotificationLoad,
      ],
      state,
    ));

  const userType = useAppSelector(getTypeOfUser);
  const notificationState = useAppSelector(getNotificationState);
  const clickedNotification = useAppSelector(getClickedNotification);
  const isPharmacy = isSelfService && userType === pharmacy;
  const isPharmacist = userType === pharmacist;

  const commonDrawerOpen = ({
    id,
    inBookingState,
    inType,
    isAutoSyncRecord,
    longTermShiftContractId,
    professionalId,
  }: ICommonDrawerOpen) => {
    openDrawer({
      eventIds: [id],
      type: inType,
      bookingState: inBookingState,
      recurrenceType: series,
      recurrenceId: '',
      fromNotification: true,
      isAutoSyncIDMap: { [id]: !!isAutoSyncRecord },
      longTermShiftContractId,
      professionalId,
    });
  };

  const visitNotifications = (notificationItem: INotificationsData) => {
    const { notificationId: id, notificationType } = notificationItem;
    const { iconType } = NotificationIdMap[notificationType] ?? {};

    if (iconType !== PositiveIcons.cancel) {
      dispatch(getNotificationLoad(id));
    } else {
      dispatch(getNotification(id));
    }

    handleMarkAsRead?.(id);
    dispatch(
      setClickedNotification({ isRequested: true, item: notificationItem }),
    );
  };

  const handleNotification = () => {
    const { startDate } = clickedNotification.item ?? { startDate: '' };
    const {
      bookingId,
      bookingState,
      status,
      workshiftId,
      isAccessible,
      professionalId,
      isAutoSyncRecord,
      longTermShiftContractId,
    } = notificationState;
    const isPendingPharmacist = isPharmacist;

    const type = statusMap[status as keyof typeof statusMap];

    const isPastDate = startDate < getTodayWithoutOffset();

    if (!(isPastDate || !isAccessible)) {
      closeNotification?.();
      navigate(calendarDashboard);

      switch (type) {
        case posted:
          if (isPharmacy) {
            commonDrawerOpen({
              id: workshiftId,
              inBookingState: bookingState,
              inType: type,
              isAutoSyncRecord,
              longTermShiftContractId,
            });
          } else if (isPharmacist) {
            openDrawer({
              eventIds: [workshiftId],
              type: PharmacistLegendVariant.workshift,
              isAutoSyncIDMap: { [workshiftId]: !!isAutoSyncRecord },
              longTermShiftContractId,
            });
          }
          break;
        case requested:
          if (isPendingPharmacist) {
            openDrawer({
              eventIds: [bookingId],
              type,
              bookingState,
              fromNotification: true,
              isAutoSyncIDMap: { [bookingId]: !!isAutoSyncRecord },
              longTermShiftContractId,
              professionalId,
            });
          } else {
            commonDrawerOpen({
              id: isSelfService ? bookingId : workshiftId,
              inBookingState: bookingState,
              inType: type,
              isAutoSyncRecord,
              longTermShiftContractId,
              professionalId,
            });
          }
          return;
        case pharmacyBooked:
          openDrawer({
            eventIds: [bookingId],
            type,
            date: startDate,
            bookingState,
            fromNotification: true,
            isAutoSyncIDMap: { [bookingId]: !!isAutoSyncRecord },
          });
          break;
        default:
          openDrawer({
            eventIds: [bookingId],
            type: type as PharmacistLegendVariant,
            bookingState,
            isAutoSyncIDMap: { [bookingId]: !!isAutoSyncRecord },
            longTermShiftContractId,
          });
          break;
      }
    }
  };

  useEffect(() => {
    if (visitSuccess) {
      dispatch(
        resetStatus([
          notificationActions.getNotification,
          notificationActions.getNotificationLoad,
        ]),
      );
    }
    if (visitSuccess && clickedNotification.isRequested) {
      dispatch(setClickedNotification(initValue));
      handleNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitSuccess]);

  return { handleActions: visitNotifications };
};

export default useNotification;
